import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ArrowPathIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { DashboardSection } from './DashboardSection';
import { Heading, Text } from '../../design-system/components/Typography';
import { Stack } from '../../design-system/components/Layout';
import { Button } from '../../design-system/components/Button';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { useExternalNews } from '../../hooks/useExternalNews';

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Row = styled.li`
  padding: ${spacing[4]} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
`;

const SourceLabel = styled(Text)`
  display: block;
  margin-bottom: ${spacing[1]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const HeadlineLink = styled.a`
  display: inline-flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  line-height: ${typography.lineHeight.tight};
  font-family: ${typography.fontFamily.primary};

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const ExternalIcon = styled(ArrowTopRightOnSquareIcon)`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin-top: 3px;
  opacity: 0.55;
`;

const DateText = styled(Text)`
  display: block;
  margin-top: ${spacing[1]};
`;

const RetryButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-family: ${typography.fontFamily.primary};

  &:hover {
    text-decoration: underline;
  }
`;

interface ExternalNewsWidgetProps {
  /** When true, shows an admin control to force-refresh the external news cache. */
  allowRefresh?: boolean;
}

export const ExternalNewsWidget: React.FC<ExternalNewsWidgetProps> = ({
  allowRefresh = false,
}) => {
  const { t } = useTranslation();
  const { feed, loading, refreshing, error, reload, refresh } = useExternalNews();

  return (
    <DashboardSection>
      <Stack direction="column" spacing="md">
        <HeaderRow>
          <Heading variant="h3" as="h2" style={{ fontSize: typography.fontSize.section, margin: 0 }}>
            {t('dashboard.news.title')}
          </Heading>
          {allowRefresh && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={refreshing}
              disabled={loading || refreshing}
              iconLeft={<ArrowPathIcon width={16} height={16} />}
              onClick={() => void refresh()}
            >
              {t('dashboard.news.refresh')}
            </Button>
          )}
        </HeaderRow>

        {loading && !feed && (
          <Text size="sm" color="secondary">
            {t('dashboard.news.loading')}
          </Text>
        )}

        {error && !feed && (
          <Stack direction="column" spacing="sm">
            <Text size="sm" color="secondary">
              {t('dashboard.news.error')}
            </Text>
            <RetryButton type="button" onClick={() => void reload()}>
              {t('dashboard.errors.retry')}
            </RetryButton>
          </Stack>
        )}

        {error && feed && allowRefresh && (
          <Text size="sm" color="secondary">
            {t('dashboard.news.refreshError')}
          </Text>
        )}

        {feed && feed.items.length === 0 && (
          <Text size="sm" color="secondary">
            {t('dashboard.news.empty')}
          </Text>
        )}

        {feed && feed.items.length > 0 && (
          <List>
            {feed.items.map(item => (
              <Row key={`${item.source}-${item.url}`}>
                <SourceLabel color="muted">
                  {item.source === 'greece'
                    ? t('dashboard.news.sourceGreece')
                    : t('dashboard.news.sourceInternational')}
                </SourceLabel>
                <HeadlineLink
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{item.title}</span>
                  <ExternalIcon aria-hidden />
                </HeadlineLink>
                <DateText size="xs" color="muted">
                  {item.dateLabel}
                </DateText>
              </Row>
            ))}
          </List>
        )}
      </Stack>
    </DashboardSection>
  );
};

export default ExternalNewsWidget;
